import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaPlus, FaMinus } from 'react-icons/fa';
import axios from '../../../../utils/axiosConfig';
import AddressInput from '../../../../components/AddressInput';
import { useAuth } from '../../../../contexts/AuthContext';

// order_type_id 4 = Shopping (errand flow, no receiver-info step needed)
const ORDER_TYPE_ID = 4;
const STEPS = ['Items', 'Contact & Location', 'Review & Confirm'];

const Shop = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [step, setStep]         = useState(0);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // Items
  const [title, setTitle]       = useState('Shopping Order');
  const [items, setItems]       = useState([{ name: '', quantity: 1, price: '' }]);
  const [note, setNote]         = useState('');

  // Contact & location
  const [contactName,  setContactName]  = useState(user?.first_name || '');
  const [contactPhone, setContactPhone] = useState(user?.phone_number || '');
  const [location, setLocation]         = useState({ description: '', lat: null, lng: null });

  // ── Items helpers ────────────────────────────────────────────────────────────
  const totalPrice = items.reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseInt(i.quantity, 10) || 0), 0);

  const updateItem = (idx, field, val) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };

  const addItem    = () => setItems(prev => [...prev, { name: '', quantity: 1, price: '' }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // ── Step guards ──────────────────────────────────────────────────────────────
  const goNext = () => {
    if (step === 0) {
      if (items.some(i => !i.name.trim())) { setError('Please fill in all item names.'); return; }
      if (!note.trim()) { setError('Please add a description for your order.'); return; }
    }
    if (step === 1) {
      if (!contactPhone.trim()) { setError('Contact phone is required.'); return; }
      if (!location.description) { setError('Please search and select a shopping location.'); return; }
    }
    setError('');
    setStep(s => s + 1);
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const itemsSummary = items.map(i => `${i.name} x${i.quantity} @ KES ${parseFloat(i.price || 0).toFixed(2)}`).join(', ');
      const description  = [
        `Items: ${itemsSummary}`,
        `Items Total: KES ${totalPrice.toFixed(2)}`,
        note ? `Note: ${note}` : '',
        contactName  ? `Contact: ${contactName}`  : '',
        contactPhone ? `Phone: ${contactPhone}` : '',
      ].filter(Boolean).join('\n');

      // Fall back to Nairobi CBD if no coordinates
      const lat = location.lat  || -1.2921;
      const lng = location.lng  || 36.8219;
      const addr = location.description || 'Nairobi CBD';

      // Step 1 — create draft
      const draftRes = await axios.post('orders/errands/draft/', {
        order_type_id:      ORDER_TYPE_ID,
        title:              title || 'Shopping Order',
        description,
        pickup_address:     addr,
        delivery_address:   addr,
        pickup_latitude:    lat,
        pickup_longitude:   lng,
        delivery_latitude:  lat,
        delivery_longitude: lng,
      });

      const orderId = draftRes.data.order_id;

      // Step 2 — confirm (shopping skips receiver-info)
      await axios.post(`orders/errands/${orderId}/confirm/`);

      navigate('/orders');
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to place order. Please try again.'
      );
    }
    setLoading(false);
  };

  return (
    <div className="page">
      <div className="card">
        {/* Progress */}
        <div className="steps">
          {STEPS.map((label, i) => (
            <div key={i} className={`step ${i === step ? 'active' : i < step ? 'done' : ''}`}>
              <div className="step-dot">{i < step ? '✓' : i + 1}</div>
              <span>{label}</span>
            </div>
          ))}
        </div>

        <h2>🛍️ Shopping Order</h2>
        {error && <div className="error-box">{error}</div>}

        {/* ── STEP 0: Items ── */}
        {step === 0 && (
          <div>
            <label className="lbl">Order Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Weekly Groceries" className="inp" />

            <p className="lbl" style={{marginBottom:8}}>Shopping Items *</p>
            {items.map((item, idx) => (
              <div key={idx} className="item-row">
                <input
                  placeholder="Item name"
                  value={item.name}
                  onChange={e => updateItem(idx, 'name', e.target.value)}
                  className="inp" style={{flex:2}}
                />
                <input
                  type="number" min="1" placeholder="Qty"
                  value={item.quantity}
                  onChange={e => updateItem(idx, 'quantity', e.target.value)}
                  className="inp" style={{flex:1}}
                />
                <input
                  type="number" min="0" step="any" placeholder="KES"
                  value={item.price}
                  onChange={e => updateItem(idx, 'price', e.target.value)}
                  className="inp" style={{flex:1}}
                />
                <div className="item-btns">
                  <button type="button" onClick={addItem} className="icon-btn add">
                    <FaPlus size={12} />
                  </button>
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(idx)} className="icon-btn rem">
                      <FaMinus size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {totalPrice > 0 && (
              <div className="total-pill">Items Total: KES {totalPrice.toFixed(2)}</div>
            )}

            <label className="lbl" style={{marginTop:12}}>Order Description / Notes *</label>
            <textarea value={note} onChange={e => setNote(e.target.value)}
              placeholder="Describe the items: weight, brand, fragility, any specific instructions..."
              className="inp" rows={3} style={{resize:'vertical'}} />

            <div className="btn-row" style={{marginTop:8}}>
              <Link to="/dashboard" className="back-btn" style={{textDecoration:'none',display:'flex',alignItems:'center',padding:'12px 20px'}}>← Back</Link>
              <button type="button" onClick={goNext} className="primary-btn">Continue →</button>
            </div>
          </div>
        )}

        {/* ── STEP 1: Contact & Location ── */}
        {step === 1 && (
          <div>
            <label className="lbl">Contact Name (optional)</label>
            <input value={contactName} onChange={e => setContactName(e.target.value)}
              placeholder="Your name or alternative contact" className="inp" />

            <label className="lbl">Contact Phone *</label>
            <input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)}
              placeholder="e.g. 0712345678" required className="inp" />

            <AddressInput
              label="Shopping Location (where to shop)"
              placeholder="Search supermarket or shopping area..."
              value={location.description}
              onSelect={loc => setLocation(loc)}
              required
            />

            <div className="btn-row">
              <button type="button" onClick={() => { setStep(0); setError(''); }} className="back-btn">← Back</button>
              <button type="button" onClick={goNext} className="primary-btn">Continue →</button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Review ── */}
        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <div className="review-box">
              <h3>Order Receipt</h3>

              <div className="r-section">
                <p className="r-label">Order</p>
                <p className="r-value">{title}</p>
              </div>

              <div className="r-section">
                <p className="r-label">Items</p>
                {items.map((item, i) => (
                  <div key={i} className="r-row">
                    <span>{item.name} × {item.quantity}</span>
                    <span>KES {(parseFloat(item.price || 0) * parseInt(item.quantity || 1, 10)).toFixed(2)}</span>
                  </div>
                ))}
                <div className="r-row" style={{fontWeight:700,borderTop:'1px solid #e5e7eb',paddingTop:8,marginTop:4}}>
                  <span>Items Total</span><span>KES {totalPrice.toFixed(2)}</span>
                </div>
              </div>

              <div className="r-section">
                <p className="r-label">Location</p>
                <p className="r-value">{location.description || '—'}</p>
              </div>

              <div className="r-section">
                <p className="r-label">Contact</p>
                <p className="r-value">{contactName && `${contactName} · `}{contactPhone}</p>
              </div>

              {note && (
                <div className="r-section">
                  <p className="r-label">Notes</p>
                  <p className="r-value">{note}</p>
                </div>
              )}
            </div>

            <p className="hint">A rider will be assigned to shop on your behalf. You will be charged the rider fee separately.</p>

            <div className="btn-row">
              <button type="button" onClick={() => { setStep(1); setError(''); }} className="back-btn">← Back</button>
              <button type="submit" disabled={loading} className="primary-btn">
                {loading ? 'Placing Order...' : 'Confirm Order ✓'}
              </button>
            </div>
          </form>
        )}
      </div>

      <style>{`
        .page{min-height:100vh;background:#f3f4f6;display:flex;align-items:flex-start;justify-content:center;padding:40px 20px;font-family:Arial,sans-serif}
        .card{background:white;border-radius:12px;padding:32px;max-width:580px;width:100%;box-shadow:0 2px 16px rgba(0,0,0,.08)}
        h2{font-size:22px;font-weight:bold;color:#111827;margin-bottom:20px}
        h3{font-size:15px;font-weight:600;color:#111827;margin-bottom:12px}
        .steps{display:flex;gap:8px;margin-bottom:24px}
        .step{display:flex;align-items:center;gap:6px;font-size:13px;color:#9ca3af;flex:1}
        .step.active{color:#3b82f6;font-weight:600}.step.done{color:#10b981}
        .step-dot{width:24px;height:24px;border-radius:50%;border:2px solid #d1d5db;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold}
        .step.active .step-dot{border-color:#3b82f6;color:#3b82f6}.step.done .step-dot{border-color:#10b981;background:#10b981;color:white}
        .error-box{background:#fee2e2;border:1px solid #f87171;color:#b91c1c;padding:10px 14px;border-radius:6px;margin-bottom:16px;font-size:14px}
        .lbl{display:block;font-size:13px;font-weight:500;color:#374151;margin-bottom:4px}
        .inp{width:100%;padding:10px 14px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;background:white;box-sizing:border-box;margin-bottom:12px}
        .inp:focus{outline:none;border-color:#3b82f6}
        .item-row{display:flex;gap:8px;align-items:flex-start;margin-bottom:8px}
        .item-row .inp{margin-bottom:0}
        .item-btns{display:flex;flex-direction:column;gap:4px;padding-top:2px}
        .icon-btn{width:26px;height:26px;border:none;border-radius:4px;cursor:pointer;display:flex;align-items:center;justify-content:center}
        .icon-btn.add{background:#dbeafe;color:#1d4ed8}.icon-btn.rem{background:#fee2e2;color:#b91c1c}
        .total-pill{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:20px;padding:6px 14px;font-size:14px;font-weight:600;color:#15803d;display:inline-block;margin-bottom:8px}
        .review-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:16px;margin-bottom:16px}
        .r-section{margin-bottom:14px;padding-bottom:10px;border-bottom:1px solid #f3f4f6}
        .r-section:last-child{border-bottom:none;margin-bottom:0;padding-bottom:0}
        .r-label{font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#9ca3af;margin-bottom:4px}
        .r-value{font-size:14px;color:#111827}
        .r-row{display:flex;justify-content:space-between;font-size:14px;color:#374151;margin-bottom:4px}
        .hint{font-size:13px;color:#6b7280;margin-bottom:16px}
        .btn-row{display:flex;gap:12px}
        .back-btn{flex:0;padding:12px 20px;border:1px solid #d1d5db;border-radius:50px;background:white;color:#374151;font-size:14px;cursor:pointer}
        .primary-btn{flex:1;padding:12px;background:#3b82f6;color:white;border:none;border-radius:50px;font-size:15px;font-weight:500;cursor:pointer;transition:background .2s}
        .primary-btn:hover{background:#2563eb}.primary-btn:disabled{background:#9ca3af;cursor:not-allowed}
        @media(max-width:480px){.item-row{flex-direction:column}.item-btns{flex-direction:row}}
      `}</style>
    </div>
  );
};

export default Shop;
